import React, { useEffect, useRef, useState } from "react";
import { BOARD_STAGE_LABELS } from "./ui";

/**
 * THE CONDITION EDITOR, ONCE.
 *
 * Lifted out of RuleBuilderPage when the call automations grew filters of their
 * own ("which calls should this remind about"), because the alternative was a
 * second editor over the same DSL. The server already refuses to let the two
 * disagree about what a legal condition IS — `validateConditions` is the one
 * authority and every config module calls it — but that guarantee stops at the
 * wire. Two editors would still have drifted on the part the server cannot see:
 * which operators a boolean offers, whether an enum gets checkboxes or a text
 * box, what a blank row looks like. One of them would have quietly grown a
 * comma-separated string where the other had checkboxes, and the rule saved from
 * it would match nothing.
 *
 * So the rule builder and the automations render the same rows from the same
 * vocabulary, and the vocabulary is the server's (`builderVocabulary().fields`,
 * carrying the operators each type accepts).
 *
 * WHAT DID NOT MOVE is the layout around the rows. The rule builder's card has a
 * live match count and a field explainer that belong to that page; the
 * automations put the rows in their own card with a preview of matching calls.
 * The row is the shared part, and the frame is each page's own.
 */

/** A blank condition row. `field` empty so the select starts on its own placeholder. */
export const BLANK = { field: "", op: "eq", value: "" };

export const OP_LABELS = {
  eq: "is",
  neq: "is not",
  gt: "is more than",
  gte: "is at least",
  lt: "is less than",
  lte: "is at most",
  between: "is between",
  in: "is one of",
  not_in: "is none of",
  is_null: "is not recorded",
  not_null: "is recorded",
};

/** Operators that take no value at all, and the ones that take two or a list. */
export const NO_VALUE = new Set(["is_null", "not_null"]);
export const TWO_VALUES = new Set(["between"]);
export const LIST_VALUES = new Set(["in", "not_in"]);

/**
 * A condition's value, from the form's strings into what the engine expects.
 *
 * Numbers must be NUMBERS: the DSL compares with `>` and `Number()`, and a string
 * "7" would still work by coercion for a number field while quietly failing an
 * `eq` against a boolean. Typed conversion here means the saved JSON says what it
 * means.
 */
export function coerce(field, op, raw, rawB) {
  if (NO_VALUE.has(op)) return undefined;
  const asOne = (v) => {
    if (!field) return v;
    if (field.type === "number") {
      const n = Number(v);
      return Number.isFinite(n) ? n : v;
    }
    if (field.type === "boolean") return v === "true" || v === true;
    return v;
  };
  if (TWO_VALUES.has(op)) return [asOne(raw), asOne(rawB)];
  if (LIST_VALUES.has(op)) {
    // Already a list — the enum checkboxes hand one straight over, and
    // round-tripping it through a comma-joined string only works while no value
    // ever contains a comma.
    if (Array.isArray(raw)) return raw.map(asOne);
    return String(raw || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map(asOne);
  }
  return asOne(raw);
}

/** And back again, so an existing rule loads into the form it was built with. */
export function decode(value, op) {
  if (TWO_VALUES.has(op)) {
    return {
      a: Array.isArray(value) ? value[0] ?? "" : "",
      b: Array.isArray(value) ? value[1] ?? "" : "",
    };
  }
  if (LIST_VALUES.has(op)) return { a: Array.isArray(value) ? value.join(", ") : "", b: "" };
  return { a: value === undefined || value === null ? "" : String(value), b: "" };
}

export function ConditionRow({ index, cond, fields, combiner, onChange, onRemove }) {
  const field = fields.find((f) => f.name === cond.field) || null;
  const ops = field ? field.ops : ["eq"];
  const decoded = decode(cond.value, cond.op);

  const set = (patch) => onChange(index, { ...cond, ...patch });

  return (
    <div className="crm-rb__cond">
      <span className="crm-rb__join crm-num">{index === 0 ? "" : combiner}</span>

      <select
        className="crm-select crm-rb__field"
        value={cond.field}
        onChange={(e) => {
          const next = fields.find((f) => f.name === e.target.value);
          // The operator is reset with the field, because the op lists differ by
          // type and keeping "is between" on a boolean would save a rule that can
          // never match.
          set({ field: e.target.value, op: next ? next.ops[0] : "eq", value: "" });
        }}
        title={field ? field.doc : undefined}
      >
        <option value="">Choose a field…</option>
        {fields.map((f) => (
          <option key={f.name} value={f.name}>
            {f.label}
          </option>
        ))}
      </select>

      <select
        className="crm-select crm-rb__op"
        value={cond.op}
        onChange={(e) => set({ op: e.target.value, value: "" })}
      >
        {ops.map((op) => (
          <option key={op} value={op}>
            {OP_LABELS[op] || op}
          </option>
        ))}
      </select>

      {NO_VALUE.has(cond.op) ? (
        <span className="crm-rb__novalue">no value needed</span>
      ) : field && field.type === "boolean" ? (
        <select
          className="crm-select crm-rb__value"
          value={String(decoded.a || "false")}
          onChange={(e) => set({ value: e.target.value === "true" })}
        >
          <option value="true">yes</option>
          <option value="false">no</option>
        </select>
      ) : field && field.type === "enum" && !LIST_VALUES.has(cond.op) ? (
        <select
          className="crm-select crm-rb__value"
          value={decoded.a}
          onChange={(e) => set({ value: e.target.value })}
        >
          <option value="">Choose…</option>
          {(field.options || []).map((o) => (
            <option key={o} value={o}>
              {BOARD_STAGE_LABELS[o] || o}
            </option>
          ))}
        </select>
      ) : field && field.type === "enum" && LIST_VALUES.has(cond.op) ? (
        /*
          Checkboxes, not a comma-separated string of keys. The stored value IS
          the key — 'proposal_sent', not 'Pricing sent' — and that is correct,
          because the key is what the database holds. But asking Gabriel to type
          it is asking him to know our schema: he thinks in "Pricing sent", and a
          typo in a free-text key produces a rule that saves and silently matches
          nothing.
        */
        <span className="crm-rb__opts">
          {(field.options || []).map((o) => {
            const picked = Array.isArray(cond.value) ? cond.value : [];
            const on = picked.includes(o);
            return (
              <label key={o} className={`crm-rb__opt${on ? " is-on" : ""}`}>
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => set({ value: on ? picked.filter((v) => v !== o) : [...picked, o] })}
                />
                {BOARD_STAGE_LABELS[o] || o}
              </label>
            );
          })}
        </span>
      ) : TWO_VALUES.has(cond.op) ? (
        <span className="crm-rb__range">
          <input
            className="crm-input"
            value={decoded.a}
            onChange={(e) => set({ value: [e.target.value, decoded.b] })}
            placeholder="from"
          />
          <span>and</span>
          <input
            className="crm-input"
            value={decoded.b}
            onChange={(e) => set({ value: [decoded.a, e.target.value] })}
            placeholder="to"
          />
        </span>
      ) : (
        <input
          className="crm-input crm-rb__value"
          value={decoded.a}
          onChange={(e) => set({ value: e.target.value })}
          placeholder={LIST_VALUES.has(cond.op) ? "comma separated" : "value"}
        />
      )}

      <button type="button" className="crm-rb__x" onClick={() => onRemove(index)} aria-label="Remove condition">
        ✕
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The whole editor, for callers that hold a condition TREE rather than a
 * combiner and a list of rows.
 * ------------------------------------------------------------------ */

/**
 * Read `{ all: [...] }` or `{ any: [...] }` into the two pieces the rows need.
 *
 * A null tree is "no filter", which for an automation means every call
 * qualifies. That is a real and useful state — it is what "remind about
 * everything on the calendar" looks like — so it round-trips rather than being
 * normalised into an empty `all`, which the engine would read the same way but
 * which would make the editor say "matches when" above nothing.
 */
export function readTree(conditions) {
  if (!conditions || typeof conditions !== "object") return { combiner: "all", rows: [] };
  const combiner = conditions.any ? "any" : "all";
  const rows = conditions[combiner] || [];
  return { combiner, rows: rows.filter((row) => row && !row.all && !row.any) };
}

/** And back. An empty list is null: no filter at all rather than a filter of nothing. */
export function writeTree(combiner, rows) {
  const kept = rows.filter((row) => row && row.field);
  if (!kept.length) return null;
  return { [combiner]: kept };
}

/**
 * `conditions` is the stored tree (or null) and `onChange` receives the next
 * one. `title` is the sentence above the rows, which differs per caller: the
 * automations say "Remind about a call when", the rescue says "Rescue a call
 * when".
 */
export function ConditionsEditor({ conditions, fields, onChange, title, emptyNote }) {
  /*
   * THE ROWS ARE LOCAL STATE, AND THEY HAVE TO BE.
   *
   * The first cut derived them from the stored tree on every render and wrote
   * straight back through `writeTree`, which drops any row without a field —
   * as it must, because `{ field: "" }` is not a condition the engine can run
   * and saving one would fail validation. The consequence was that "+ add
   * condition" did nothing at all: the blank row it appended was filtered out
   * before it could be rendered, every time, so the button looked broken.
   *
   * So a half-written row lives HERE until it is complete enough to mean
   * something, and only complete rows are published upward. That is the same
   * split the rule builder has always had (it holds `conds` in its own state),
   * and it is the reason this editor keeps state at all.
   *
   * `emitted` is what stops the round trip from wiping the blank row out again:
   * when the tree coming back down is the one we just sent up, it is our own
   * echo and the local rows are already right. Anything else — a different
   * automation loaded, Discard pressed — is a genuine outside change and does
   * reset them.
   */
  const initial = readTree(conditions);
  const [combiner, setCombinerState] = useState(initial.combiner);
  const [rows, setRowsState] = useState(initial.rows);
  const emitted = useRef(JSON.stringify(writeTree(initial.combiner, initial.rows) ?? null));

  useEffect(() => {
    if (JSON.stringify(conditions ?? null) === emitted.current) return;
    const next = readTree(conditions);
    setCombinerState(next.combiner);
    setRowsState(next.rows);
  }, [conditions]);

  const publish = (nextCombiner, nextRows) => {
    const tree = writeTree(nextCombiner, nextRows);
    emitted.current = JSON.stringify(tree ?? null);
    onChange(tree);
  };

  const setRows = (next) => {
    setRowsState(next);
    publish(combiner, next);
  };
  const setCombiner = (next) => {
    setCombinerState(next);
    publish(next, rows);
  };

  return (
    <div className="crm-auto-conds">
      <div className="crm-rb__cardhead">
        <span className="crm-rb__title">{title}</span>
        <div className="crm-pillradio crm-rb__combiner">
          {["all", "any"].map((c) => (
            <button
              key={c}
              type="button"
              className={combiner === c ? "sel" : ""}
              onClick={() => setCombiner(c)}
              disabled={rows.length < 2}
              title={rows.length < 2 ? "Add a second condition to choose" : undefined}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 && <p className="crm-hint">{emptyNote}</p>}

      <div className="crm-rb__conds">
        {rows.map((cond, i) => (
          <ConditionRow
            key={i}
            index={i}
            cond={cond}
            fields={fields}
            combiner={combiner === "all" ? "and" : "or"}
            onChange={(idx, next) => setRows(rows.map((c, j) => (j === idx ? next : c)))}
            onRemove={(idx) => setRows(rows.filter((_, j) => j !== idx))}
          />
        ))}
      </div>

      <button type="button" className="crm-rb__add" onClick={() => setRows([...rows, BLANK])}>
        + add condition
      </button>
    </div>
  );
}
